import { useTranslation } from 'react-i18next'
import { Section } from '../ui/Section'
import { Container } from '../ui/Container'
import { SectionTitle } from '../ui/SectionTitle'
import { motion } from 'framer-motion'
import { Clock, Building2, Award, Truck, ChevronLeft, ChevronRight, Brain, Flame, Target, Search } from 'lucide-react'
import { useRef } from 'react'

const milestones = [
  {
    year: '1972',
    title: 'La fondazione',
    description: 'Giuseppe Chiggiato fonda l\'azienda con un chiaro obiettivo: offrire un servizio di trasporto affidabile e professionale su tutto il territorio nazionale.',
    image: '/images/history-founder.jpg',
    icon: Building2,
  },
  {
    year: '2009',
    title: 'La seconda generazione',
    description: 'Franco e Luca Chiggiato subentrano nella gestione, portando nuova energia e innovazione. L\'azienda amplia i servizi e consolida la presenza nei trasporti controllati.',
    image: '/images/history-team.jpg',
    icon: Building2,
  },
  {
    year: '2015',
    title: 'Certificazioni internazionali',
    description: 'Ottenute le certificazioni IFS Logistics, ISO 9001, ISO 45001, TAPA TSR 1+ e GDP, garantendo standard di eccellenza in qualità, sicurezza e conformità.',
    image: '/images/company-operations.jpg',
    icon: Award,
  },
  {
    year: '2023',
    title: 'Flotta moderna e copertura nazionale',
    description: 'Con sedi operative a Padova, Lucca e Bari, la flotta di Chiggiato SpA è oggi tra le più moderne del settore, pronta a servire ogni destinazione in Italia.',
    image: '/images/company-fleet.jpg',
    icon: Truck,
  },
]

const teamMembers = [
  {
    name: 'Giuseppe Chiggiato',
    role: 'Fondatore',
    image: '/images/history-founder.jpg',
  },
  {
    name: 'Franco Chiggiato',
    role: 'Amministratore',
    image: '/images/history-team.jpg',
  },
  {
    name: 'Luca Chiggiato',
    role: 'Presidente',
    image: '/images/history-team.jpg',
  },
]

export function CompanyHistory() {
  const { t } = useTranslation()
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  return (
    <>
      {/* Company History Section */}
      <Section>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <SectionTitle title={t('history.intro.heading', 'Radici solide, sguardo al futuro')} />
              <div className="space-y-4 text-brand-text">
                <p>{t('about.intro.body')}</p>
              </div>
            </div>

            <div className="relative">
              <img
                src="/images/history-founder.jpg"
                alt="Chiggiato Storia"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>

        </Container>
      </Section>

      {/* Values */}
      <Section variant="light">
        <Container>
          <h2 className="text-2xl md:text-4xl font-bold text-brand-dark mb-12 text-center">
            {t('about.values.heading', 'I nostri valori')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { key: 'rationality', Icon: Brain },
              { key: 'passion', Icon: Flame },
              { key: 'precision', Icon: Target },
              { key: 'curiosity', Icon: Search },
            ].map(({ key, Icon }, index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white p-6 md:p-8 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 text-center"
              >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-brand-red/10">
                  <Icon className="h-7 w-7 text-brand-red" />
                </div>
                <h3 className="text-lg font-bold text-brand-dark mb-3">
                  {t(`about.values.${key}.title`)}
                </h3>
                <p className="text-brand-text leading-relaxed">
                  {t(`about.values.${key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Timeline Section */}
      <Section>
        <Container>
          <SectionTitle
            title={t('history.timeline.heading', 'Le tappe della nostra storia')}
            subtitle="Il percorso di crescita di Chiggiato SpA dal 1972 ad oggi"
            centered
          />

          <div className="relative mt-16">
            {/* Timeline line - desktop - animata con scorrimento */}
            <motion.div
              className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-5 h-full bg-gradient-to-b from-brand-red via-red-500 to-brand-red rounded-full origin-top"
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 2, ease: 'easeOut' }}
            />

            {/* Timeline items */}
            <div className="space-y-16">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -80 : 80 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className={`relative flex flex-col md:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Content - a sinistra o destra a seconda della posizione */}
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16 md:text-left'}`}>
                    <div
                      className={`bg-white rounded-xl shadow-lg p-6 max-w-lg ${
                        index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'
                      }`}
                    >
                      {/* Immagine */}
                      <div className="relative aspect-video overflow-hidden rounded-lg mb-4">
                        <img
                          src={milestone.image}
                          alt={milestone.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="inline-flex items-center gap-2 text-brand-red font-bold text-2xl mb-3">
                        <Clock className="w-6 h-6" />
                        {milestone.year}
                      </span>
                      <h3 className="text-xl font-semibold text-brand-dark mb-2">{milestone.title}</h3>
                      <p className="text-brand-text">{milestone.description}</p>
                    </div>
                  </div>

                  {/* Center dot - animato con pulse */}
                  <motion.div
                    className="hidden md:flex w-20 h-20 bg-brand-red rounded-full items-center justify-center z-10 shadow-xl shrink-0 border-4 border-white"
                    initial={{ scale: 0, rotate: -180 }}
                    whileInView={{ scale: 1, rotate: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.5, delay: index * 0.2, type: 'spring' }}
                  >
                    <milestone.icon className="w-10 h-10 text-white" />
                  </motion.div>

                  {/* Empty space for alignment */}
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Team Section */}
      <Section variant="light">
        <Container>
          <div className="text-center mb-12">
            <SectionTitle
              title="La famiglia Chiggiato"
              subtitle="Le persone che rendono Chiggiato SpA un partner affidabile"
              centered
            />
          </div>

          {/* Team Carousel */}
          <div className="relative">
            {/* Navigation Buttons */}
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-brand-red hover:text-white transition-colors"
              aria-label="Scorri a sinistra"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-brand-red hover:text-white transition-colors"
              aria-label="Scorri a destra"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Scrollable Container */}
            <div
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto pb-4 px-4 snap-x snap-mandatory scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="flex-shrink-0 w-[280px] snap-start"
                >
                  <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 h-full">
                    {/* Image Container */}
                    <div className="relative aspect-[4/5] overflow-hidden bg-gray-100">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          const parent = target.parentElement
                          if (parent) {
                            parent.classList.add('flex', 'items-center', 'justify-center')
                            const fallback = document.createElement('div')
                            fallback.className = 'flex flex-col items-center justify-center text-brand-dark/40'
                            fallback.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`
                            parent.appendChild(fallback)
                          }
                        }}
                      />
                    </div>

                    {/* Info */}
                    <div className="p-5 text-center">
                      <h3 className="text-lg font-bold text-brand-dark mb-1">{member.name}</h3>
                      <p className="text-brand-red font-medium text-sm">{member.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Scroll Indicator */}
            <div className="flex justify-center gap-2 mt-4">
              {teamMembers.map((_, index) => (
                <div
                  key={index}
                  className="w-2 h-2 rounded-full bg-brand-red/30"
                />
              ))}
            </div>
          </div>
        </Container>
      </Section>
    </>
  )
}
